import React, { useEffect, useRef } from "react";
import { Form, Input, Modal, Button, Flex, Typography, TimePicker, notification } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { isNilOrEmpty } from "@salespark/toolkit";
import { useAuth } from "services/Auth";
import { offlineHandler } from "utils/offlineCache";
import { locationTracker } from "utils/location/geolocation";
import { LOCATION_ACTIONS } from "constants/location";

const { TextArea } = Input;
const { Text, Title } = Typography;

const StartStopService = ({ visible, data, action, selectedService, setSelectedService, onClose, onConfirm }) => {
  const [form] = Form.useForm();
  const inputRef = useRef(null);
  const { auth } = useAuth();
  const apiClient = offlineHandler(auth.data);
  const authUserName = auth?.data?.name || "Unknown User";

  useEffect(() => {
    if (visible && data) {
      form.setFieldsValue({
        start_date_effective: data.start_date ? dayjs(data.start_date) : null,
        end_date_effective: data.end_date ? dayjs(data.end_date) : null,
        observations: data.observations ?? "",
      });
    }

    return () => {
      form.resetFields();
    };
  }, [visible, data, form]);

  const isStart = action === "start";

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const isStartAction = action === "start";
      const effectiveKey = isStartAction ? "start_date_effective" : "end_date_effective";
      const effectiveValue = values[effectiveKey];

      if (isNilOrEmpty(effectiveValue)) {
        notification.error({
          message: "Erro",
          description: isStartAction ? "Data de Início inválida" : "Data de Fim inválida",
        });
        return;
      }

      const payload = {
        observations: values.observations?.trim() || null,
        serviceStatus: isStartAction ? "running" : "completed",
        [effectiveKey]: dayjs(effectiveValue).toDate(),
      };

      await setSelectedService({
        ...selectedService,
        ...payload,
      });
      if (isStartAction) {
        const trackingContext = {
          service_id_raw: selectedService?.service_id_raw || null,
          equipment_id: selectedService?.equipment?.[0]?._id || null,
          user: auth?.data?._id || null,
        };
        // Start the GPS watcher and capture the service-start position immediately.
        // Both are best-effort (they request the location permission) and must not block
        // starting the service if GPS is unavailable or the permission is denied.
        locationTracker.startTracking(trackingContext);
        locationTracker.captureActionLocation({ ...trackingContext, action: LOCATION_ACTIONS.SERVICE_START });

        // Send info capture to the server for debugging purposes
        await apiClient.post("/info-capture", {
          extraData: {
            location: "src/views/app-views/services/StartStopService.jsx/handleSubmit",
            timestamp: new Date().toISOString(),
          },
          info: {
            location: "src/views/app-views/services/StartStopService.jsx/handleSubmit",
            timestamp: new Date().toISOString(),
            service_id: selectedService?.service_id_raw || null,
            equipment: selectedService?.equipment?.[0]?._id || null,
            payload: payload,
          },
          subject: "Boats: Mobile Service",
          message: `Boats: Service started (${authUserName})`,
        });
        onClose();
      } else {
        onConfirm(payload);
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (isNilOrEmpty(data)) return null;

  const getTimeWindow = (baseDate, minutes = 30) => {
    const base = dayjs(baseDate);
    return {
      min: base.subtract(minutes, "minute"),
      max: base.add(minutes, "minute"),
    };
  };

  const getDisabledTime = (baseDate) => {
    if (!baseDate) return {};

    const { min, max } = getTimeWindow(baseDate);

    return {
      disabledHours: () => Array.from({ length: 24 }, (_, h) => (h < min.hour() || h > max.hour() ? h : null)).filter(Boolean),

      disabledMinutes: (selectedHour) => {
        if (selectedHour < min.hour() || selectedHour > max.hour()) {
          return Array.from({ length: 60 }, (_, i) => i);
        }

        return Array.from({ length: 60 }, (_, m) =>
          (selectedHour === min.hour() && m < min.minute()) || (selectedHour === max.hour() && m > max.minute()) ? m : null,
        ).filter(Boolean);
      },
    };
  };

  return (
    <Modal
      title={<Title level={3}>{isStart ? "Iniciar Serviço" : "Terminar Serviço"}</Title>}
      open={visible}
      onCancel={onClose}
      width={650}
      destroyOnHidden
      afterOpenChange={(open) => {
        if (open && inputRef.current) {
          setTimeout(() => {
            inputRef.current.focus();
          }, 50);
        }
      }}
      footer={[
        <Button key="back" size="large" onClick={onClose} style={{ borderRadius: 8 }}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" size="large" danger={!isStart} onClick={handleSubmit} style={{ borderRadius: 8, paddingInline: 40 }}>
          {isStart ? "Confirmar Início" : "Confirmar Fim"}
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 12, padding: "12px", background: "#f5f5f5", borderRadius: 8 }}>
        <Flex justify="space-between" align="center">
          <div>
            <Text type="secondary">Serviço</Text>
            <div style={{ fontWeight: "bold", fontSize: 16 }}>{data.service_id_raw}</div>
          </div>
          <Flex gap="large">
            <Flex vertical align="end">
              <Text type="secondary">
                <ClockCircleOutlined /> Planeado
              </Text>
              <Text strong>
                {dayjs(data.start_date).format("HH:mm")} – {dayjs(data.end_date).format("HH:mm")}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </div>

      <Form form={form} layout="vertical" preserve={false}>
        <Flex gap="md">
          {isStart === true ? (
            <Form.Item label="Hora de Início" name="start_date_effective" style={{ flex: 1 }} rules={[{ required: isStart }]}>
              <TimePicker
                size="large"
                disabledTime={() => getDisabledTime(data.start_date)}
                format="HH:mm"
                previewValue={false}
                showNow={false}
                hideDisabledOptions
                changeOnScroll={false}
              />
            </Form.Item>
          ) : (
            <Form.Item label="Hora de Fim" name="end_date_effective" style={{ flex: 1 }} rules={[{ required: !isStart }]}>
              <TimePicker
                size="large"
                disabledTime={() => getDisabledTime(data.end_date)}
                format="HH:mm"
                previewValue={false}
                showNow={false}
                hideDisabledOptions
                changeOnScroll={false}
                ref={inputRef}
              />
            </Form.Item>
          )}
          {!isStart && (
            <Flex align="center" gap={8} style={{ flex: 1 }}>
              <Text type="secondary">Passageiros embarcados</Text>
              <div
                style={{
                  minWidth: 40,
                  height: 40,
                  borderRadius: 8,
                  background: data.embarked_count > 0 ? "#f6ffed" : "f96370",
                  border: "1px solid #52c41a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  fontWeight: 600,
                }}
              >
                {data.embarked_count}
              </div>
            </Flex>
          )}
        </Flex>

        <Form.Item label="Observações" name="observations">
          <TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default StartStopService;
